import { createClient } from '@supabase/supabase-js';

const SUPABASE_ANON = process.env.REACT_APP_SUPABASE_ANON_KEY || '';

// ── PROXY: all calls go through aspreo.com/api/sb → Vercel rewrites to supabase.co
// This bypasses Indian ISP (Tata/Jio/Airtel) blocks on supabase.co
// vercel.json: { "rewrites": [{ "source": "/api/sb/:path*", "destination": "https://jsvvmeudvywckdwljefc.supabase.co/:path*" }] }
const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

// www.aspreo.com is the primary domain — aspreo.com 307-redirects to www.
// Always hardcode www in production so the proxy URL is always same-origin.
const PROD_URL    = 'https://www.aspreo.com';
// REACT_APP_SUPABASE_URL lets local dev point at a throwaway/local Supabase
// instance instead of the real project below. Unset in normal use.
const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL || (isLocal
  ? 'https://jsvvmeudvywckdwljefc.supabase.co'
  : `${PROD_URL}/api/sb`);

const SITE_URL = isLocal ? 'http://localhost:3000' : PROD_URL;

export const IS_CONFIGURED = true;

// ── Single shared session promise ─────────────────────────────────────────────
// Prevents 429 "Too Many Requests" on token refresh.
// All 27+ parallel DB calls in loadAll() each trigger getSession() internally.
// By caching one refresh per 4 minutes, we collapse them into a single call.
let _sessionCache = null;
let _sessionExpiry = 0;

export const supabase = createClient(
  SUPABASE_URL,
  SUPABASE_ANON || 'placeholder',
  {
    auth: {
      autoRefreshToken:   true,
      persistSession:     true,
      detectSessionInUrl: true,
      lock: async (_name, _timeout, fn) => fn(),
    },
  }
);

// Patch: deduplicate rapid getSession calls within 4-minute window
const _origGetSession = supabase.auth.getSession.bind(supabase.auth);
supabase.auth.getSession = async () => {
  const now = Date.now();
  if (_sessionCache && now < _sessionExpiry) return _sessionCache;
  // Only one real call at a time — if one is in flight, wait for it
  if (supabase.auth._pendingSession) return supabase.auth._pendingSession;
  supabase.auth._pendingSession = _origGetSession().then(result => {
    _sessionCache = result;
    _sessionExpiry = Date.now() + 4 * 60 * 1000; // cache 4 min
    supabase.auth._pendingSession = null;
    return result;
  }).catch(err => {
    supabase.auth._pendingSession = null;
    throw err;
  });
  return supabase.auth._pendingSession;
};

// Every REST call (.from(), .rpc(), etc.) reads getSession() internally to
// build its Authorization header. Without this, a session cached *before*
// sign-in (e.g. the null session from the very first page load) stays cached
// for up to 4 minutes after signing in — every request in that window sends
// the anon key instead of the user's token, so RLS sees them as anonymous
// (silent 401s/403s/0-row results) even though the UI shows them logged in.
supabase.auth.onAuthStateChange(() => {
  _sessionCache = null;
  _sessionExpiry = 0;
});

// ── Storage ───────────────────────────────────────────────────────────────────
export const BUCKETS = {
  files:   process.env.REACT_APP_STORAGE_BUCKET || 'user-filed',
  avatars: process.env.REACT_APP_AVATAR_BUCKET  || 'avatars',
  visions: process.env.REACT_APP_VISION_BUCKET  || 'vision-images',
};

// ── Safe uploadFile with bucket existence check ───────────────────────────────
// If bucket not found, falls back gracefully instead of crashing

export const getUser       = () => supabase.auth.getUser();
export const getSession    = () => supabase.auth.getSession();

export const signInEmail   = (email, pw) =>
  supabase.auth.signInWithPassword({ email, password: pw });

export const signUpEmail   = (email, pw, meta) =>
  supabase.auth.signUp({
    email,
    password: pw,
    options: {
      data: meta,
      // emailRedirectTo uses Supabase Site URL setting — no need to pass it
      // explicitly (avoids 500 if the URL isn't yet whitelisted in Supabase)
    },
  });

export const signOut       = () => supabase.auth.signOut();

export const resetPassword = (email) =>
  supabase.auth.resetPasswordForEmail(email, {
    redirectTo: `${SITE_URL}/`,
  });

export async function uploadFile(bucket, path, file) {
  const { data, error } = await supabase.storage.from(bucket).upload(path, file, { upsert: true });
  if (error) {
    // Bucket not found — provide clear error message
    if (error.message?.includes('Bucket not found') || error.statusCode === '404') {
      throw new Error(`Storage bucket "${bucket}" not found. Please create it in Supabase Storage dashboard.`);
    }
    throw error;
  }
  try {
    const { data: pub } = supabase.storage.from(bucket).getPublicUrl(data.path);
    if (pub?.publicUrl) return pub.publicUrl;
  } catch {}
  const { data: signed, error: signErr } = await supabase.storage.from(bucket).createSignedUrl(data.path, 60 * 60 * 24 * 365 * 10);
  if (signErr) throw signErr;
  return signed.signedUrl;
}

export async function deleteFile(bucket, path) {
  const { error } = await supabase.storage.from(bucket).remove([path]);
  if (error) throw error;
}

export function subscribeToTable(table, userId, callback) {
  return supabase
    .channel(`${table}-${userId}`)
    .on('postgres_changes', { event: '*', schema: 'public', table, filter: `user_id=eq.${userId}` }, callback)
    .subscribe();
}

// ── DB helpers ────────────────────────────────────────────────────────────────
export const db = {
  getProfile:    (uid)  => supabase.from('profiles').select('*').eq('id', uid).single(),
  upsertProfile: (data) => supabase.from('profiles').upsert(data).select(),

  getGoals:    (uid)  => supabase.from('goals').select('*').eq('user_id', uid).order('priority'),
  upsertGoal:  (data) => supabase.from('goals').upsert(data, { onConflict: 'id' }).select(),
  deleteGoal:  (id)   => supabase.from('goals').delete().eq('id', id),

  getAllTasks:  (uid)  => supabase.from('tasks').select('*').eq('user_id', uid),
  upsertTask:  (data) => supabase.from('tasks').upsert(data, { onConflict: 'id' }).select(),
  updateTask:  (id,d) => supabase.from('tasks').update(d).eq('id', id).select(),
  deleteTask:  (id)   => supabase.from('tasks').delete().eq('id', id),

  getDailyEntry:   (uid, dt) => supabase.from('daily_entries').select('*').eq('user_id', uid).eq('entry_date', dt).maybeSingle(),
  upsertDailyEntry:(data)    => supabase.from('daily_entries').upsert(data, { onConflict: 'user_id,entry_date' }).select(),

  getCheckinDefs:  (uid)     => supabase.from('checkin_defs').select('*').eq('user_id', uid).order('sort_order'),
  upsertCheckinDef:(data)    => supabase.from('checkin_defs').upsert(data, { onConflict: 'user_id,id' }).select(),
  deleteCheckinDef:(uid, id) => supabase.from('checkin_defs').delete().eq('user_id', uid).eq('id', id),
  getDailyCheckins:(uid, dt) => supabase.from('daily_checkins').select('*').eq('user_id', uid).eq('checkin_date', dt),
  upsertDailyCi:   (data)    => supabase.from('daily_checkins').upsert(data, { onConflict: 'user_id,checkin_date,checkin_id' }).select(),

  getHabits:    (uid)  => supabase.from('habits').select('*').eq('user_id', uid),
  upsertHabit:  (data) => supabase.from('habits').upsert(data, { onConflict: 'id' }).select(),
  deleteHabit:  (id)   => supabase.from('habits').delete().eq('id', id),
  getHabitLog:  (uid, dt) => supabase.from('habit_log').select('*').eq('user_id', uid).eq('log_date', dt),
  upsertHabitLog:(data)   => supabase.from('habit_log').upsert(data, { onConflict: 'user_id,habit_id,log_date' }).select(),

  getExpenses:   (uid)  => supabase.from('expenses').select('*').eq('user_id', uid).order('expense_date', { ascending: false }),
  upsertExpense: (data) => supabase.from('expenses').upsert(data, { onConflict: 'id' }).select(),
  deleteExpense: (id)   => supabase.from('expenses').delete().eq('id', id),
  getIncome:     (uid)  => supabase.from('income').select('*').eq('user_id', uid).order('income_date', { ascending: false }),
  upsertIncome:  (data) => supabase.from('income').upsert(data, { onConflict: 'id' }).select(),
  deleteIncome:  (id)   => supabase.from('income').delete().eq('id', id),

  getIdeas:    (uid)  => supabase.from('ideas').select('*').eq('user_id', uid).order('created_at', { ascending: false }),
  upsertIdea:  (data) => supabase.from('ideas').upsert(data, { onConflict: 'id' }).select(),
  deleteIdea:  (id)   => supabase.from('ideas').delete().eq('id', id),

  getBooks:    (uid)  => supabase.from('books').select('*').eq('user_id', uid),
  upsertBook:  (data) => supabase.from('books').upsert(data, { onConflict: 'id' }).select(),
  deleteBook:  (id)   => supabase.from('books').delete().eq('id', id),

  getSubs:    (uid)  => supabase.from('subscriptions').select('*').eq('user_id', uid),
  upsertSub:  (data) => supabase.from('subscriptions').upsert(data, { onConflict: 'id' }).select(),
  deleteSub:  (id)   => supabase.from('subscriptions').delete().eq('id', id),

  getReminders:   (uid)  => supabase.from('reminders').select('*').eq('user_id', uid).eq('dismissed', false),
  upsertReminder: (data) => supabase.from('reminders').upsert(data, { onConflict: 'id' }).select(),
  dismissReminder:(id)   => supabase.from('reminders').update({ dismissed: true }).eq('id', id).select(),
  deleteReminder: (id)   => supabase.from('reminders').delete().eq('id', id),

  getVisions:    (uid)  => supabase.from('visions').select('*').eq('user_id', uid).order('created_at'),
  upsertVision:  (data) => supabase.from('visions').upsert(data, { onConflict: 'id' }).select(),
  deleteVision:  (id)   => supabase.from('visions').delete().eq('id', id),
  getBanner:     (uid)  => supabase.from('vision_board_banner').select('*').eq('user_id', uid).maybeSingle(),
  upsertBanner:  (data) => supabase.from('vision_board_banner').upsert(data, { onConflict: 'user_id' }).select(),
  getPillars:    (uid)  => supabase.from('vision_pillars').select('*').eq('user_id', uid).order('sort_order'),
  upsertPillar:  async (data) => {
    try { return await supabase.from('vision_pillars').upsert(data, { onConflict: 'user_id,id' }).select(); }
    catch(e) { return { data: null, error: null }; }
  },
  deletePillar:  (uid, id) => supabase.from('vision_pillars').delete().eq('user_id', uid).eq('id', id),

  getWeekPlan:    (uid, wk) => supabase.from('week_plans').select('*').eq('user_id', uid).eq('week_key', wk),
  upsertWeekPlan: (data)    => supabase.from('week_plans').upsert(data, { onConflict: 'user_id,week_key,day_idx' }).select(),
  getMonthPlan:   (uid, mk) => supabase.from('month_plans').select('*').eq('user_id', uid).eq('month_key', mk),
  upsertMonthPlan:(data)    => supabase.from('month_plans').upsert(data, { onConflict: 'user_id,month_key,plan_type' }).select(),

  getContentPillars:   (uid)  => supabase.from('content_pillars').select('*').eq('user_id', uid).order('created_at'),
  upsertContentPillar: (data) => supabase.from('content_pillars').upsert(data, { onConflict: 'id' }).select(),
  deleteContentPillar: (id)   => supabase.from('content_pillars').delete().eq('id', id),
  getContentPosts:     (uid)  => supabase.from('content_posts').select('*').eq('user_id', uid).order('created_at', { ascending: false }),
  upsertContentPost:   (data) => supabase.from('content_posts').upsert(data, { onConflict: 'id' }).select(),
  deleteContentPost:   (id)   => supabase.from('content_posts').delete().eq('id', id),
  getContentStrategy:  (uid)  => supabase.from('content_strategy').select('*').eq('user_id', uid).maybeSingle(),
  upsertContentStrategy:(data)=> supabase.from('content_strategy').upsert(data, { onConflict: 'user_id' }).select(),

  getFiles:       (uid)  => supabase.from('user_files').select('*').eq('user_id', uid).order('created_at', { ascending: false }),
  insertFile:     (data) => supabase.from('user_files').insert(data).select(),
  deleteFileRecord:(id)  => supabase.from('user_files').delete().eq('id', id),

  getVaultFolders:   (uid)  => supabase.from('vault_folders').select('*').eq('user_id', uid).order('created_at'),
  upsertVaultFolder: (data) => supabase.from('vault_folders').upsert(data, { onConflict: 'id' }).select(),
  deleteVaultFolder: (id)   => supabase.from('vault_folders').delete().eq('id', id),
  getVaultLinks:     (uid)  => supabase.from('vault_links').select('*').eq('user_id', uid).order('created_at'),
  upsertVaultLink:   (data) => supabase.from('vault_links').upsert(data, { onConflict: 'id' }).select(),
  deleteVaultLink:   (id)   => supabase.from('vault_links').delete().eq('id', id),

  getNotifications: (uid) => supabase.from('notifications').select('*').eq('user_id', uid).order('created_at', { ascending: false }).limit(50),
  markNotifRead:    (id)  => supabase.from('notifications').update({ read: true }).eq('id', id),
  markAllNotifRead: (uid) => supabase.from('notifications').update({ read: true }).eq('user_id', uid),
  deleteNotif:      (id)  => supabase.from('notifications').delete().eq('id', id),

  subscribeNewsletter: (email, name) => supabase.from('newsletter_subscribers').upsert({ email, name }, { onConflict: 'email' }).select(),

  getProjects:       (uid)  => supabase.from('projects').select('*').eq('user_id', uid).order('created_at'),
  getMemberProjects: (uid)  => supabase.from('project_members').select('project_id').eq('user_id', uid).eq('status', 'accepted'),
  getPendingInvitesByEmail: (email) => supabase.from('project_members').select('id,project_id,invited_email,status').eq('invited_email', email.toLowerCase()).eq('status', 'pending'),
  upsertProject:     (data) => supabase.from('projects').upsert(data, { onConflict: 'id' }).select(),
  deleteProject:     (id)   => supabase.from('projects').delete().eq('id', id),

  getProjectTasks:   (uid)  => supabase.from('project_tasks').select('*').eq('user_id', uid).order('created_at', { ascending: true }),
  getProjectTasksByIds: (ids) => supabase.from('project_tasks').select('*').in('project_id', ids).order('created_at'),
  upsertProjectTask: (data) => supabase.from('project_tasks').upsert(data, { onConflict: 'id' }).select(),
  updateProjectTask: (id,d) => supabase.from('project_tasks').update(d).eq('id', id).select(),
  deleteProjectTask: (id)   => supabase.from('project_tasks').delete().eq('id', id),

  // ── Project task activity logs ──────────────────────────────────────────────
  getTaskLogs:   (taskId) => supabase.from('project_task_logs').select('*').eq('task_id', taskId).order('created_at', { ascending: false }).limit(50),
  insertTaskLog: (data)   => supabase.from('project_task_logs').insert(data),

  getSpecialDates:   (uid)  => supabase.from('special_dates').select('*').eq('user_id', uid).order('month').order('day'),
  upsertSpecialDate: (data) => supabase.from('special_dates').upsert(data, { onConflict: 'id' }).select(),
  deleteSpecialDate: (id)   => supabase.from('special_dates').delete().eq('id', id),

  getContentStrategies:    (uid)  => supabase.from('content_strategies').select('*').eq('user_id', uid).order('created_at'),
  upsertContentStrategy2:  (data) => supabase.from('content_strategies').upsert(data, { onConflict: 'id' }).select(),
  deleteContentStrategy2:  (id)   => supabase.from('content_strategies').delete().eq('id', id),

  archiveHabit:   (id) => supabase.from('habits').update({ archived: true,  archived_at: new Date().toISOString() }).eq('id', id).select(),
  unarchiveHabit: (id) => supabase.from('habits').update({ archived: false, archived_at: null }).eq('id', id).select(),
  archiveGoal:    (id) => supabase.from('goals').update({ archived: true,   archived_at: new Date().toISOString() }).eq('id', id).select(),
  unarchiveGoal:  (id) => supabase.from('goals').update({ archived: false,  archived_at: null }).eq('id', id).select(),
  archiveBook:    (id) => supabase.from('books').update({ archived: true,   archived_at: new Date().toISOString() }).eq('id', id).select(),
  unarchiveBook:  (id) => supabase.from('books').update({ archived: false,  archived_at: null }).eq('id', id).select(),

  getVaultSession:    (uid, token) => supabase.from('vault_sessions').select('*').eq('user_id', uid).eq('device_token', token).gt('expires_at', new Date().toISOString()).maybeSingle(),
  upsertVaultSession: (data)       => supabase.from('vault_sessions').upsert(data, { onConflict: 'user_id,device_token' }).select(),
  deleteVaultSession: (uid, token) => supabase.from('vault_sessions').delete().eq('user_id', uid).eq('device_token', token),

  getRecurringTasks:    (uid)  => supabase.from('recurring_tasks').select('*').eq('user_id', uid).order('start_date'),
  upsertRecurringTask:  (data) => supabase.from('recurring_tasks').upsert(data, { onConflict: 'id' }).select(),
  deleteRecurringTask:  (id)   => supabase.from('recurring_tasks').delete().eq('id', id),

  getSharedTemplates:      (type) => supabase.from('shared_templates').select('*').eq('type', type).eq('is_public', true).order('import_count', { ascending: false }),
  getTemplateById:         (id)   => supabase.from('shared_templates').select('*').eq('id', id).eq('is_public', true).maybeSingle(),
  getMyTemplates:          (uid)  => supabase.from('shared_templates').select('*').eq('user_id', uid).order('created_at', { ascending: false }),
  upsertSharedTemplate:    (data) => supabase.from('shared_templates').upsert(data, { onConflict: 'id' }).select(),
  deleteSharedTemplate:    (id)   => supabase.from('shared_templates').delete().eq('id', id),
  incrementTemplateImport: (id)   => supabase.rpc('increment_template_imports', { template_id: id }),

  getShareCard:    (id)   => supabase.from('share_cards').select('*').eq('id', id).maybeSingle(),
  upsertShareCard: (data) => supabase.from('share_cards').upsert(data, { onConflict: 'id' }).select(),
  deleteShareCard: (id)   => supabase.from('share_cards').delete().eq('id', id),

  // Project members (collaboration)
  getProjectMembers:    (uid)  => supabase.from('project_members').select('*'),
  inviteProjectMember:  (data) => supabase.from('project_members').insert(data).select(),
  updateProjectMember:  (id,d) => supabase.from('project_members').update(d).eq('id', id).select(),
  acceptInviteByEmail:  (email, uid, status='accepted') => supabase.from('project_members').update({ status, ...(uid ? { user_id: uid } : {}) }).eq('invited_email', email.toLowerCase()).eq('status', 'pending').select(),
  deleteProjectMember:  (id)   => supabase.from('project_members').delete().eq('id', id),
  getProjectMembersByProject: (pid) => supabase.from('project_members').select('*,profiles(username,full_name,avatar_url)').eq('project_id', pid),

  // Social feed
  getFeedPosts:   (uid)  => supabase.from('feed_posts').select('*, feed_likes(user_id)').order('created_at', { ascending: false }).limit(50),
  getFellowFeed:  (uid)  => supabase.from('feed_posts').select('*,profiles(username,full_name,avatar_url),feed_likes(user_id)').in('user_id', [uid]).order('created_at', { ascending: false }).limit(100),
  insertFeedPost: (data) => supabase.from('feed_posts').insert(data).select(),
  deleteFeedPost: (id)   => supabase.from('feed_posts').delete().eq('id', id),
  toggleFeedLike: async (postId, userId) => {
    // Check if already liked
    const { data: existing } = await supabase.from('feed_likes').select('id').eq('post_id', postId).eq('user_id', userId).maybeSingle();
    if (existing) {
      // Unlike: remove like row, decrement count
      await supabase.from('feed_likes').delete().eq('id', existing.id);
      await supabase.rpc('decrement_likes', { post_id: postId });
      return { data: null, error: null, liked: false };
    } else {
      // Like: insert like row, increment count
      await supabase.from('feed_likes').insert({ post_id: postId, user_id: userId });
      await supabase.rpc('increment_likes', { post_id: postId });
      return { data: null, error: null, liked: true };
    }
  },

  // Fellows (accountability partners)
  insertNotification: (data) => supabase.from('notifications').insert(data).select(),
  getFellows:      (uid)  => supabase.from('fellows').select('*').or(`requester_id.eq.${uid},addressee_id.eq.${uid}`),
  insertFellow:    (data) => supabase.from('fellows').insert(data).select(),
  updateFellow:    (id,d) => supabase.from('fellows').update(d).eq('id', id).select(),
  deleteFellow:    (id)   => supabase.from('fellows').delete().eq('id', id),
  searchProfiles:  (username, uid) => supabase.from('profiles')
    .select('id,username,full_name,avatar_url,email')
    .or(`username.ilike.%${username}%,full_name.ilike.%${username}%`)
    .neq('id', uid || '00000000-0000-0000-0000-000000000000')
    .limit(10),

  // ── SAAS PLANS + SUBSCRIPTIONS ──────────────────────────────────────────
  getPlans:           ()        => supabase.from('plans').select('*').eq('is_active',true).order('sort_order'),
  getAllPlans:         ()        => supabase.from('plans').select('*').order('sort_order'),
  updatePlan:         (id,d)    => supabase.from('plans').update({...d,updated_at:new Date().toISOString()}).eq('id',id).select(),
  getUserPlan:        (uid)     => supabase.from('user_plans').select('*,plans(*)').eq('user_id',uid).maybeSingle(),
  upsertUserPlan:     (uid,d)   => supabase.from('user_plans').upsert({user_id:uid,...d,updated_at:new Date().toISOString()},{onConflict:'user_id'}).select(),
  getAllUserPlans:     ()        => supabase.from('user_plans').select('*,profiles(id,email,full_name,avatar_url,created_at),plans(name,price_monthly)').order('created_at',{ascending:false}),
  // Workspaces
  getWorkspaces:      (uid)     => supabase.from('workspaces').select('*').eq('owner_id',uid),
  insertWorkspace:    (d)       => supabase.from('workspaces').insert(d).select(),
  updateWorkspace:    (id,d)    => supabase.from('workspaces').update(d).eq('id',id).select(),
  deleteWorkspace:    (id)      => supabase.from('workspaces').delete().eq('id',id),
  getWorkspaceMembers:(oid)     => supabase.from('workspace_members').select('*').eq('owner_id',oid),
  insertWorkspaceMember:(d)     => supabase.from('workspace_members').insert(d).select(),
  updateWorkspaceMember:(id,d)  => supabase.from('workspace_members').update(d).eq('id',id).select(),
  deleteWorkspaceMember:(id)    => supabase.from('workspace_members').delete().eq('id',id),
  getMyWorkspaceMemberships:(email) => supabase.from('workspace_members').select('*,workspaces(name,owner_id)').eq('member_email',email).eq('status','active'),
  // Admin
  getAdminStats:      ()        => supabase.from('profiles').select('id,email,full_name,role,created_at,last_view',{count:'exact'}),
  insertAdminLog:     (d)       => supabase.from('admin_logs').insert(d),
  getAdminLogs:       ()        => supabase.from('admin_logs').select('*,profiles(full_name,email)').order('created_at',{ascending:false}).limit(100),

  // ── PAYMENT AUTOMATION ──────────────────────────────────────────────────
  getProducts:      (uid)  => supabase.from('products').select('*').eq('user_id', uid).order('position').order('created_at', { ascending: false }),
  insertProduct:    (data) => supabase.from('products').insert(data).select(),
  updateProduct:    (id,d) => supabase.from('products').update(d).eq('id', id).select(),
  deleteProduct:    (id)   => supabase.from('products').delete().eq('id', id),
  getProductBySlug: (slug) => supabase.from('products').select('*').eq('slug', slug).eq('is_active', true).maybeSingle(),

  getOrders:        (uid)  => supabase.from('orders').select('*, products(name,type)').eq('user_id', uid).order('created_at', { ascending: false }),
  insertOrder:      (data) => supabase.from('orders').insert(data).select(),
  updateOrder:      (id,d) => supabase.from('orders').update(d).eq('id', id).select(),
  getOrdersByProduct:(pid) => supabase.from('orders').select('*').eq('product_id', pid).order('created_at', { ascending: false }),

  getAutomations:   (uid)  => supabase.from('automations').select('*').eq('user_id', uid).order('created_at', { ascending: false }),
  insertAutomation: (data) => supabase.from('automations').insert(data).select(),
  updateAutomation: (id,d) => supabase.from('automations').update(d).eq('id', id).select(),
  deleteAutomation: (id)   => supabase.from('automations').delete().eq('id', id),

  // ── PUBLIC SHARE TOKEN LOOKUP ────────────────────────────────────────────
  getInvoiceByToken:  (token) => supabase.from('invoices').select('*').eq('share_token', token).eq('is_shared', true).maybeSingle(),
  getProposalByToken: (token) => supabase.from('proposals').select('*').eq('share_token', token).eq('is_shared', true).maybeSingle(),

  // ── SERVICES LIBRARY + BUSINESS PROFILE ─────────────────────────────────
  getServices:        (uid)  => supabase.from('services').select('*').eq('user_id', uid).eq('is_active', true).order('position').order('name'),
  getSenderProfiles:  (uid)  => supabase.from('sender_profiles').select('*').eq('user_id', uid).order('sort_order', { ascending: true }),
  insertSenderProfile:(data) => supabase.from('sender_profiles').insert(data).select(),
  updateSenderProfile:(id,d) => supabase.from('sender_profiles').update(d).eq('id', id).select(),
  deleteSenderProfile:(id)   => supabase.from('sender_profiles').delete().eq('id', id),
  insertService:      (data) => supabase.from('services').insert(data).select(),
  updateService:      (id,d) => supabase.from('services').update(d).eq('id', id).select(),
  deleteService:      (id)   => supabase.from('services').delete().eq('id', id),
  getBizProfile:      (uid)  => supabase.from('profiles').select('biz_name,biz_tagline,biz_about,biz_address,biz_phone,biz_email,biz_website,biz_gstin,biz_pan,biz_logo_url,biz_bank_name,biz_bank_acc,biz_bank_ifsc,biz_bank_type,biz_terms,inv_prefix,proforma_prefix,prop_prefix,razorpay_key_id,razorpay_key_secret,prop_default_executive_summary,prop_default_why_us,prop_default_next_steps,prop_default_best_practices,prop_placeholders,prop_theme_bg,prop_theme_primary,prop_theme_font').eq('id', uid).maybeSingle(),
  updateBizProfile:   (uid,d)=> supabase.from('profiles').update(d).eq('id', uid).select(),

  // ── BUSINESS MODULE ──────────────────────────────────────────────────────
  getLeads:          (uid)  => supabase.from('leads').select('*').eq('user_id', uid).order('position').order('created_at', { ascending: false }),
  insertLead:        (data) => supabase.from('leads').insert(data).select(),
  updateLead:        (id,d) => supabase.from('leads').update(d).eq('id', id).select(),
  deleteLead:        (id)   => supabase.from('leads').delete().eq('id', id),
  getClients:        (uid)  => supabase.from('clients').select('*').eq('user_id', uid).order('created_at', { ascending: false }),
  insertClient:      (data) => supabase.from('clients').insert(data).select(),
  updateClient:      (id,d) => supabase.from('clients').update(d).eq('id', id).select(),
  deleteClient:      (id)   => supabase.from('clients').delete().eq('id', id),
  getProposals:      (uid)  => supabase.from('proposals').select('*').eq('user_id', uid).order('created_at', { ascending: false }),
  insertProposal:    (data) => supabase.from('proposals').insert(data).select(),
  updateProposal:    (id,d) => supabase.from('proposals').update(d).eq('id', id).select(),
  deleteProposal:    (id)   => supabase.from('proposals').delete().eq('id', id),
  getInvoices:       (uid)  => supabase.from('invoices').select('*').eq('user_id', uid).order('created_at', { ascending: false }),
  insertInvoice:     (data) => supabase.from('invoices').insert(data).select(),
  updateInvoice:     (id,d) => supabase.from('invoices').update(d).eq('id', id).select(),
  deleteInvoice:     (id)   => supabase.from('invoices').delete().eq('id', id),
  getSheetLinks:     (uid)  => supabase.from('sheet_links').select('*').eq('user_id', uid),
  insertSheetLink:   (data) => supabase.from('sheet_links').insert(data).select(),
  updateSheetLink:   (id,d) => supabase.from('sheet_links').update(d).eq('id', id).select(),
  deleteSheetLink:   (id)   => supabase.from('sheet_links').delete().eq('id', id),
  getConsultants:    (uid)  => supabase.from('consultants').select('*').eq('user_id', uid).order('created_at', { ascending: false }),
  insertConsultant:  (data) => supabase.from('consultants').insert(data).select(),
  updateConsultant:  (id,d) => supabase.from('consultants').update(d).eq('id', id).select(),
  deleteConsultant:  (id)   => supabase.from('consultants').delete().eq('id', id),

  // ── SOCIAL MEDIA MODULE ──────────────────────────────────────────────────
  getSocialAccounts:   (uid)  => supabase.from('social_accounts').select('*').eq('user_id', uid).order('connected_at', { ascending: false }),
  insertSocialAccount: (data) => supabase.from('social_accounts').insert(data).select(),
  updateSocialAccount: (id,d) => supabase.from('social_accounts').update(d).eq('id', id).select(),
  deleteSocialAccount: (id)   => supabase.from('social_accounts').delete().eq('id', id),
  getDMTemplates:    (uid)  => supabase.from('dm_templates').select('*').eq('user_id', uid).order('created_at', { ascending: false }),
  insertDMTemplate:  (data) => supabase.from('dm_templates').insert(data).select(),
  updateDMTemplate:  (id,d) => supabase.from('dm_templates').update(d).eq('id', id).select(),
  deleteDMTemplate:  (id)   => supabase.from('dm_templates').delete().eq('id', id),
  getViralLinks:     (uid)  => supabase.from('viral_links').select('*').eq('user_id', uid).order('created_at', { ascending: false }),
  insertViralLink:   (data) => supabase.from('viral_links').insert(data).select(),
  updateViralLink:   (id,d) => supabase.from('viral_links').update(d).eq('id', id).select(),
  deleteViralLink:   (id)   => supabase.from('viral_links').delete().eq('id', id),
  getAllEngagement:   (uid)  => supabase.from('post_engagement').select('*').eq('user_id', uid).order('fetched_at', { ascending: false }),
  upsertEngagement:  (data) => supabase.from('post_engagement').insert(data).select(),

  // ── MEETING SCHEDULER ────────────────────────────────────────────────────
  getMeetingTypes:    (uid)  => supabase.from('meeting_types').select('*').eq('user_id', uid).order('position'),
  insertMeetingType:  (data) => supabase.from('meeting_types').insert(data).select(),
  updateMeetingType:  (id,d) => supabase.from('meeting_types').update(d).eq('id', id),
  deleteMeetingType:  (id)   => supabase.from('meeting_types').delete().eq('id', id),
  getAvailability:    (uid)  => supabase.from('availability_slots').select('*').eq('user_id', uid),
  upsertAvailability: (data) => supabase.from('availability_slots').upsert(data, { onConflict: 'user_id,day_of_week' }).select(),
  getMeetings:        (uid)  => supabase.from('meetings').select('*, meeting_types(title,duration_mins,color)').eq('host_id', uid).order('start_time'),
  insertMeeting:      (data) => supabase.from('meetings').insert(data).select(),
  updateMeeting:      (id,d) => supabase.from('meetings').update(d).eq('id', id).select(),
  deleteMeeting:      (id)   => supabase.from('meetings').delete().eq('id', id),


  // ── WIKI PAGES ──────────────────────────────────────────────────────────────
  getWikiPages:     (uid)  => supabase.from('wiki_pages').select('*').eq('user_id', uid).order('sort_order'),
  upsertWikiPage:   (data) => supabase.from('wiki_pages').upsert(data, { onConflict: 'id' }).select(),
  deleteWikiPage:   (id)   => supabase.from('wiki_pages').delete().eq('id', id),

  // ── KNOWLEDGE BASE (Second Brain) ────────────────────────────────────────────
  getKnowledgeDomains:     (uid)  => supabase.from('knowledge_domains').select('*').eq('user_id', uid).order('sort_order'),
  upsertKnowledgeDomain:   (data) => supabase.from('knowledge_domains').upsert(data, { onConflict: 'id' }).select(),
  deleteKnowledgeDomain:   (id)   => supabase.from('knowledge_domains').delete().eq('id', id),

  getKnowledgeCollections:   (uid)  => supabase.from('knowledge_collections').select('*').eq('user_id', uid).order('sort_order'),
  upsertKnowledgeCollection: (data) => supabase.from('knowledge_collections').upsert(data, { onConflict: 'id' }).select(),
  deleteKnowledgeCollection: (id)   => supabase.from('knowledge_collections').delete().eq('id', id),

  getKnowledgeTags:     (uid)  => supabase.from('knowledge_tags').select('*').eq('user_id', uid).order('name'),
  upsertKnowledgeTag:   (data) => supabase.from('knowledge_tags').upsert(data, { onConflict: 'user_id,name' }).select(),
  deleteKnowledgeTag:   (id)   => supabase.from('knowledge_tags').delete().eq('id', id),

  getKnowledgeItems:    (uid)  => supabase.from('knowledge_items').select('*').eq('user_id', uid).order('updated_at', { ascending: false }),
  upsertKnowledgeItem:  (data) => supabase.from('knowledge_items').upsert(data, { onConflict: 'id' }).select(),
  deleteKnowledgeItem:  (id)   => supabase.from('knowledge_items').delete().eq('id', id),
  touchKnowledgeItemView: (id, viewCount) => supabase.from('knowledge_items').update({ last_viewed_at: new Date().toISOString(), view_count: viewCount }).eq('id', id),

  getKnowledgeItemTags:    (uid)  => supabase.from('knowledge_item_tags').select('*, knowledge_items!inner(user_id)').eq('knowledge_items.user_id', uid),
  addKnowledgeItemTag:     (itemId, tagId) => supabase.from('knowledge_item_tags').upsert({ item_id: itemId, tag_id: tagId }, { onConflict: 'item_id,tag_id' }).select(),
  removeKnowledgeItemTag:  (itemId, tagId) => supabase.from('knowledge_item_tags').delete().eq('item_id', itemId).eq('tag_id', tagId),

  getKnowledgeRelated:    (uid)  => supabase.from('knowledge_related').select('*').eq('user_id', uid),
  addKnowledgeRelated:    (data) => supabase.from('knowledge_related').insert(data).select(),
  deleteKnowledgeRelated: (id)   => supabase.from('knowledge_related').delete().eq('id', id),

  getKnowledgeAttachments:   (uid)  => supabase.from('knowledge_attachments').select('*, knowledge_items!inner(user_id)').eq('knowledge_items.user_id', uid),
  addKnowledgeAttachment:    (data) => supabase.from('knowledge_attachments').insert(data).select(),
  deleteKnowledgeAttachment: (id)   => supabase.from('knowledge_attachments').delete().eq('id', id),

  getKnowledgeVersions:   (itemId) => supabase.from('knowledge_versions').select('*').eq('item_id', itemId).order('created_at', { ascending: false }),
  addKnowledgeVersion:    (data)   => supabase.from('knowledge_versions').insert(data).select(),
  deleteKnowledgeVersion: (id)     => supabase.from('knowledge_versions').delete().eq('id', id),

  // ── REFLECTIONS ─────────────────────────────────────────────────────────────
  getReflections:   (uid)  => supabase.from('reflections').select('*').eq('user_id', uid).order('entry_date', { ascending: false }),
  upsertReflection: (data) => supabase.from('reflections').upsert(data, { onConflict: 'id' }).select(),
  deleteReflection: (id)   => supabase.from('reflections').delete().eq('id', id),

  // ── JOURNAL (Reflection & Journal module) ───────────────────────────────────
  getJournalEntries:     (uid)  => supabase.from('journal_entries').select('*').eq('user_id', uid).order('entry_date', { ascending: false }).order('created_at', { ascending: false }),
  addJournalEntry:       (data) => supabase.from('journal_entries').insert(data).select(),
  updateJournalEntry:    (id,d) => supabase.from('journal_entries').update(d).eq('id', id).select(),
  deleteJournalEntry:    (id)   => supabase.from('journal_entries').delete().eq('id', id),

  getJournalTags:        (uid)  => supabase.from('journal_tags').select('*').eq('user_id', uid).order('name'),
  upsertJournalTag:      (data) => supabase.from('journal_tags').upsert(data, { onConflict: 'user_id,name' }).select(),
  deleteJournalTag:      (id)   => supabase.from('journal_tags').delete().eq('id', id),

  getJournalEntryTags:   (uid)  => supabase.from('journal_entry_tags').select('*, journal_entries!inner(user_id)').eq('journal_entries.user_id', uid),
  addJournalEntryTag:    (entryId, tagId) => supabase.from('journal_entry_tags').upsert({ entry_id: entryId, tag_id: tagId }, { onConflict: 'entry_id,tag_id' }).select(),
  removeJournalEntryTag: (entryId, tagId) => supabase.from('journal_entry_tags').delete().eq('entry_id', entryId).eq('tag_id', tagId),

  getJournalAttachments:   (uid)  => supabase.from('journal_attachments').select('*, journal_entries!inner(user_id)').eq('journal_entries.user_id', uid),
  addJournalAttachment:    (data) => supabase.from('journal_attachments').insert(data).select(),
  deleteJournalAttachment: (id)   => supabase.from('journal_attachments').delete().eq('id', id),

  getJournalVersions:   (entryId) => supabase.from('journal_versions').select('*').eq('entry_id', entryId).order('created_at', { ascending: false }),
  addJournalVersion:    (data)    => supabase.from('journal_versions').insert(data).select(),
  deleteJournalVersion: (id)      => supabase.from('journal_versions').delete().eq('id', id),

  // ── FITNESS & DIET ──────────────────────────────────────────────────────────
  getFitnessMeals:      (uid)      => supabase.from('fitness_meals').select('*').eq('user_id', uid).order('log_date', { ascending: false }).order('created_at', { ascending: false }),
  insertFitnessMeal:    (data)     => supabase.from('fitness_meals').insert(data).select(),
  deleteFitnessMeal:    (id)       => supabase.from('fitness_meals').delete().eq('id', id),
  getFitnessBodyStats:  (uid)      => supabase.from('fitness_body_stats').select('*').eq('user_id', uid).order('log_date', { ascending: false }),
  upsertFitnessBodyStat:(data)     => supabase.from('fitness_body_stats').upsert(data, { onConflict: 'id' }).select(),
  deleteFitnessBodyStat:(id)       => supabase.from('fitness_body_stats').delete().eq('id', id),
  getFitnessGoals:      (uid)      => supabase.from('fitness_goals').select('*').eq('user_id', uid).maybeSingle(),
  upsertFitnessGoals:   (data)     => supabase.from('fitness_goals').upsert(data, { onConflict: 'user_id' }).select(),

  // ── NUTRITION (Fitness & Indian Diet — Phase 1) ─────────────────────────────
  getDietFoods:          (uid)  => supabase.from('diet_foods').select('*').or(`is_global.eq.true,user_id.eq.${uid}`).order('name'),
  addDietFood:           (data) => supabase.from('diet_foods').insert(data).select(),
  updateDietFood:        (id,d) => supabase.from('diet_foods').update(d).eq('id', id).select(),
  deleteDietFood:        (id)   => supabase.from('diet_foods').delete().eq('id', id),

  getDietMealLogs:       (uid)  => supabase.from('diet_meal_logs').select('*').eq('user_id', uid).order('log_date', { ascending: false }).order('logged_at', { ascending: false }),
  addDietMealLog:        (data) => supabase.from('diet_meal_logs').insert(data).select(),
  deleteDietMealLog:     (id)   => supabase.from('diet_meal_logs').delete().eq('id', id),

  getDietWaterLogs:      (uid)  => supabase.from('diet_water_logs').select('*').eq('user_id', uid).order('log_date', { ascending: false }),
  addDietWaterLog:       (data) => supabase.from('diet_water_logs').insert(data).select(),
  deleteDietWaterLog:    (id)   => supabase.from('diet_water_logs').delete().eq('id', id),

  getDietSupplements:    (uid)  => supabase.from('diet_supplements').select('*').eq('user_id', uid).order('created_at'),
  addDietSupplement:     (data) => supabase.from('diet_supplements').insert(data).select(),
  updateDietSupplement:  (id,d) => supabase.from('diet_supplements').update(d).eq('id', id).select(),
  deleteDietSupplement:  (id)   => supabase.from('diet_supplements').delete().eq('id', id),

  getDietSupplementLogs: (uid)  => supabase.from('diet_supplement_logs').select('*').eq('user_id', uid).order('log_date', { ascending: false }),
  addDietSupplementLog:  (data) => supabase.from('diet_supplement_logs').upsert(data, { onConflict: 'supplement_id,log_date' }).select(),
  deleteDietSupplementLog:(id)  => supabase.from('diet_supplement_logs').delete().eq('id', id),

  getDietTargets:        (uid)  => supabase.from('diet_targets').select('*').eq('user_id', uid).maybeSingle(),
  upsertDietTargets:     (data) => supabase.from('diet_targets').upsert(data, { onConflict: 'user_id' }).select(),

  getDietMealPlan:       (uid)  => supabase.from('diet_meal_plan').select('*').eq('user_id', uid).order('day_of_week').order('sort_order'),
  addDietMealPlanItem:   (data) => supabase.from('diet_meal_plan').insert(data).select(),
  deleteDietMealPlanItem:(id)   => supabase.from('diet_meal_plan').delete().eq('id', id),

  // ── WORKOUTS (Fitness & Indian Diet — Phase 2) ──────────────────────────────
  getWorkoutExercises:      (uid)  => supabase.from('workout_exercises').select('*').or(`is_global.eq.true,user_id.eq.${uid}`).order('name'),
  addWorkoutExercise:       (data) => supabase.from('workout_exercises').insert(data).select(),
  updateWorkoutExercise:    (id,d) => supabase.from('workout_exercises').update(d).eq('id', id).select(),
  deleteWorkoutExercise:    (id)   => supabase.from('workout_exercises').delete().eq('id', id),

  getWorkoutPlans:          (uid)  => supabase.from('workout_plans').select('*').eq('user_id', uid).order('created_at'),
  addWorkoutPlan:           (data) => supabase.from('workout_plans').insert(data).select(),
  updateWorkoutPlan:        (id,d) => supabase.from('workout_plans').update(d).eq('id', id).select(),
  deleteWorkoutPlan:        (id)   => supabase.from('workout_plans').delete().eq('id', id),

  getWorkoutPlanExercises:  (uid)  => supabase.from('workout_plan_exercises').select('*').eq('user_id', uid).order('sort_order'),
  addWorkoutPlanExercise:   (data) => supabase.from('workout_plan_exercises').insert(data).select(),
  deleteWorkoutPlanExercise:(id)   => supabase.from('workout_plan_exercises').delete().eq('id', id),

  getWorkoutSchedule:       (uid)  => supabase.from('workout_schedule').select('*').eq('user_id', uid).order('day_of_week'),
  addWorkoutScheduleItem:   (data) => supabase.from('workout_schedule').insert(data).select(),
  deleteWorkoutScheduleItem:(id)   => supabase.from('workout_schedule').delete().eq('id', id),

  getWorkoutLogs:           (uid)  => supabase.from('workout_logs').select('*').eq('user_id', uid).order('log_date', { ascending: false }).order('created_at', { ascending: false }),
  addWorkoutLog:            (data) => supabase.from('workout_logs').insert(data).select(),
  updateWorkoutLog:         (id,d) => supabase.from('workout_logs').update(d).eq('id', id).select(),
  deleteWorkoutLog:         (id)   => supabase.from('workout_logs').delete().eq('id', id),

  getWorkoutLogExercises:   (uid)  => supabase.from('workout_log_exercises').select('*').eq('user_id', uid).order('sort_order'),
  addWorkoutLogExercise:    (data) => supabase.from('workout_log_exercises').insert(data).select(),
  deleteWorkoutLogExercise: (id)   => supabase.from('workout_log_exercises').delete().eq('id', id),

  getWorkoutLogSets:        (uid)  => supabase.from('workout_log_sets').select('*').eq('user_id', uid).order('set_number'),
  addWorkoutLogSet:         (data) => supabase.from('workout_log_sets').insert(data).select(),
  updateWorkoutLogSet:      (id,d) => supabase.from('workout_log_sets').update(d).eq('id', id).select(),
  deleteWorkoutLogSet:      (id)   => supabase.from('workout_log_sets').delete().eq('id', id),

  // ── BODY COMPOSITION + HEALTH GOALS (Fitness & Indian Diet — Phase 3) ───────
  getBodyProfile:           (uid)  => supabase.from('body_profile').select('*').eq('user_id', uid).maybeSingle(),
  upsertBodyProfile:        (data) => supabase.from('body_profile').upsert(data, { onConflict: 'user_id' }).select(),

  getBodyCompositionLogs:   (uid)  => supabase.from('body_composition_logs').select('*').eq('user_id', uid).order('log_date', { ascending: false }),
  addBodyCompositionLog:    (data) => supabase.from('body_composition_logs').insert(data).select(),
  deleteBodyCompositionLog: (id)   => supabase.from('body_composition_logs').delete().eq('id', id),

  getHealthGoals:           (uid)  => supabase.from('health_goals').select('*').eq('user_id', uid).order('created_at', { ascending: false }),
  addHealthGoal:            (data) => supabase.from('health_goals').insert(data).select(),
  updateHealthGoal:         (id,d) => supabase.from('health_goals').update(d).eq('id', id).select(),
  deleteHealthGoal:         (id)   => supabase.from('health_goals').delete().eq('id', id),

  // ── DEEP WORK ────────────────────────────────────────────────────────────────
  getDeepWorkSessions:   (uid)  => supabase.from('deep_work_sessions').select('*').eq('user_id', uid).order('session_date', { ascending: false }).order('created_at', { ascending: false }),
  insertDeepWorkSession: (data) => supabase.from('deep_work_sessions').insert(data).select(),
  deleteDeepWorkSession: (id)   => supabase.from('deep_work_sessions').delete().eq('id', id),

  // ── CALENDAR EVENTS (week time-blocking) ────────────────────────────────────
  getCalendarEvents:    (uid)  => supabase.from('calendar_events').select('*').eq('user_id', uid).order('starts_at', { ascending: true }),
  insertCalendarEvent:  (data) => supabase.from('calendar_events').insert(data).select(),
  deleteCalendarEvent:  (id)   => supabase.from('calendar_events').delete().eq('id', id),

  // ── PROJECT TIME LOGS ────────────────────────────────────────────────────────
  getTimeLogs:          (uid)  => supabase.from('project_time_logs').select('*').eq('user_id', uid).order('created_at', { ascending: false }),
  getTimeLogsByProject: (pid)  => supabase.from('project_time_logs').select('*').eq('project_id', pid).order('created_at', { ascending: false }),
  insertTimeLog:        (data) => supabase.from('project_time_logs').insert(data).select(),

  // ── USER INTEGRATIONS ────────────────────────────────────────────────────────
  getIntegrations:   (uid)  => supabase.from('user_integrations').select('*').eq('user_id', uid),
  upsertIntegration: (data) => supabase.from('user_integrations').upsert(data, { onConflict: 'user_id,type' }).select(),
  deleteIntegration: (uid, type) => supabase.from('user_integrations').delete().eq('user_id', uid).eq('type', type),

  // ── BUSINESS EXPENSES ────────────────────────────────────────────────────────
  getBusinessExpenses:   (uid)  => supabase.from('business_expenses').select('*').eq('user_id', uid).order('expense_date', { ascending: false }),
  insertBusinessExpense: (data) => supabase.from('business_expenses').insert(data).select(),
  deleteBusinessExpense: (id)   => supabase.from('business_expenses').delete().eq('id', id),

  // ── SIGN IN WITH USERNAME ────────────────────────────────────────────────────
  // Username availability check (public lookup — no RLS needed for username column)
  checkUsername:   (username) => supabase.from('profiles').select('id').eq('username', username).maybeSingle(),
  signInWithUsername: async (username, password) => {
    // Look up email from username, then sign in with email+password
    const { data } = await supabase.from('profiles').select('email').eq('username', username).maybeSingle();
    if (!data?.email) return { error: { message: 'Username not found.' } };
    return supabase.auth.signInWithPassword({ email: data.email, password });
  },
};
